/** Constants for the search feature. */

// ─── Filter selection limits ────────────────────────────────────────────────

/** Maximum number of items that can be selected in Namespace or String Group filters */
export const MAX_SELECTION_COUNT = 100;

// ─── Fetch-all pagination limits ────────────────────────────────────────────

/** Maximum pages to fetch when paginating to guard against infinite loops */
export const MAX_PAGES = 50;

/** Maximum pageSize allowed by the Smithy PaginationParameters (range max: 100) */
export const MAX_PAGE_SIZE = 100;

/**
 * Upper bound on the number of items a fetch-all can return before pagination
 * is capped. Used in the user-facing truncation hints below.
 */
export const MAX_FETCHED_ITEMS = MAX_PAGES * MAX_PAGE_SIZE;

const MAX_FETCHED_ITEMS_LABEL = MAX_FETCHED_ITEMS.toLocaleString('en-US');

/**
 * Hints surfaced on a filter dropdown when its fetch-all stopped at the page
 * cap. Without these the cap is silent in production (the console.warn is
 * dev-only), so the user has no way to tell that the option list is partial.
 */
export const TRUNCATION_HINTS = {
  NAMESPACES: `Showing first ${MAX_FETCHED_ITEMS_LABEL} namespaces; some options may be missing.`,
  STRING_GROUPS: `Showing first ${MAX_FETCHED_ITEMS_LABEL} string groups per namespace; some options may be missing.`,
  CX_FAMILIES: `Showing first ${MAX_FETCHED_ITEMS_LABEL} CX families; some options may be missing.`,
} as const;

// ─── Search submission validation ───────────────────────────────────────────

/**
 * Reason shown to the user when source-string search is blocked because no
 * criterion has been supplied. Shared by its two consumers so they can never
 * drift:
 *   - `SearchBar`'s row-level `FormField` `constraintText` (the pre-submit gate)
 *   - `SearchResultsTable`'s criteria gate, the backstop for paths that re-fire
 *     an existing search (sort change, pagination, Retry)
 *
 * This restriction is a UI-layer product decision: the backend deliberately
 * supports an empty `queryText` ("browse mode"). See `hasMinimumSearchCriteria`
 * in `searchFilterUtils.ts`.
 */
export const MIN_SEARCH_CRITERIA_MESSAGE =
  'Enter a search term or select at least one filter to search.';

// ─── Stable references ──────────────────────────────────────────────────────
/**
 * Stable empty array reference to avoid creating a new [] on every render,
 * which would cause spurious useEffect re-fires when used as a dependency.
 */
export const EMPTY_ARRAY: string[] = [];
